import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import Menus from "./pages/Menus";
import Rentals from "./pages/Rentals";
import Contracts from "./pages/Contracts";
import ContractPreview from "./pages/ContractPreview";
import CalendarPage from "./pages/CalendarPage";
import Equipment from "./pages/Equipment";
import BEO from "./pages/BEO";
import PackingList from "./pages/PackingList";
import Invoices from "./pages/Invoices";
import Staff from "./pages/Staff";
import StaffScheduler from "./pages/StaffScheduler";
import Inventory from "./pages/Inventory";
import Decor from "./pages/Decor";
import MenuPreview from "./pages/MenuPreview";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import Login from "./pages/Login";
import QuoteCalculator from "./pages/QuoteCalculator";
import QuoteRequest from "./pages/QuoteRequest";
import QuoteManagement from "./pages/QuoteManagement";
import ClientPortalLogin from "./pages/ClientPortalLogin";
import ClientPortalDashboard from "./pages/ClientPortalDashboard";
import EventReports from "./pages/EventReports";
import StandaloneBEOs from "./pages/StandaloneBEOs";
import WeeklyEventSheet from "./pages/WeeklyEventSheet";
import POSMenuManager from "./pages/POSMenuManager";
import POS from "./pages/POS";
import FloorplanEditor from "./pages/FloorplanEditor";
import CocktailCoachWebsite from "./pages/CocktailCoachWebsite";
import ClubServicesRollup from "./pages/ClubServicesRollup";
import { Toaster } from "./components/ui/toaster";
import { useLocation } from "react-router-dom";

// Updates document.title based on the current route.
// Public site pages show "Cocktail Coach — ..."; admin pages show "Willow Ridge — ...".
const PUBLIC_PATHS = ['/', '/cocktail-coach', '/quote', '/quote-request', '/client-portal-login'];
const TitleManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const isPublic = PUBLIC_PATHS.some(p => pathname === p) || pathname.startsWith('/client-portal');
    document.title = isPublic
      ? 'Cocktail Coach — Willow Ridge Catering'
      : 'Willow Ridge — Admin';
  }, [pathname]);
  return null;
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5dc] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] mx-auto"></div>
          <p className="mt-4 text-[#1e3a5f]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Layout with Sidebar
const AppLayout = ({ children }) => {
  return (
    <div className="flex print:block">
      <Sidebar />
      <div className="flex-1 min-w-0 print:w-full lg:pl-0 pl-0" data-testid="main-content">
        <div className="lg:hidden h-12" /> {/* Spacer for mobile hamburger */}
        {children}
      </div>
    </div>
  );
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5dc] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] mx-auto"></div>
          <p className="mt-4 text-[#1e3a5f]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <TitleManager />
      <Routes>
      {/* Public routes - no login required */}
      <Route path="/" element={<CocktailCoachWebsite />} />
      <Route path="/cocktail-coach" element={<CocktailCoachWebsite />} />
      <Route path="/quote" element={<QuoteCalculator />} />
      <Route path="/quote-request" element={<QuoteRequest />} />
      
      {/* Client Portal routes - separate auth */}
      <Route path="/client-portal" element={<ClientPortalLogin />} />
      <Route path="/client-portal/contract/:contractId" element={<ClientPortalDashboard />} />
      
      {/* Login route */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Clients />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menus"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Menus />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rentals"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Rentals />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Contracts />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CalendarPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/weekly-sheet"
        element={
          <ProtectedRoute>
            <AppLayout>
              <WeeklyEventSheet />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/pos-menu"
        element={
          <ProtectedRoute>
            <AppLayout>
              <POSMenuManager />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipment"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Equipment />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/beo"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BEO />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/packing"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PackingList />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/floorplan"
        element={
          <ProtectedRoute>
            <FloorplanEditor />
          </ProtectedRoute>
        }
      />

      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Analytics />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quote-management"
        element={
          <ProtectedRoute>
            <AppLayout>
              <QuoteManagement />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contract-preview"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ContractPreview />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Invoices />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Staff />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-scheduler"
        element={
          <ProtectedRoute>
            <AppLayout>
              <StaffScheduler />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Inventory />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/decor"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Decor />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu-preview"
        element={
          <ProtectedRoute>
            <MenuPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Settings />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/event-reports"
        element={
          <ProtectedRoute>
            <AppLayout>
              <EventReports />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/standalone-beos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <StandaloneBEOs />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pos"
        element={
          <ProtectedRoute>
            <AppLayout>
              <POS />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/club-services-rollup"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ClubServicesRollup />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard or login */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
